import type { ComponentProps } from 'react';

import { AzureConnectTenantWizard } from '@/app/(account)/global-management/azure-tenants/azureConnectTenantWizard';
import { AwsConnectAccountWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/aws/awsConnectAccountWizard';
import { AzureOnboardingWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/azure/azureOnboardingWizard';
import { AzureConnectRestoreWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/azure/bring-your-own/azureConnectRestoreWizard';
import { AzureConnectSourceWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/azure/bring-your-own/azureConnectSourceWizard';
import { AzureOwnCloudOnboardingWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/azure/bring-your-own/azureOwnCloudOnboardingWizard';
import { GcpConnectAccountWizard } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/gcp/gcpConnectAccountWizard';
import { InventoryInstancePanel } from '@/app/(dashboard)/inventory/inventoryInstancePanel';
import { BackupJobsCard } from '@/app/(dashboard)/jobs/[tab]/backupJobsCard';
import { RestoreJobsCard } from '@/app/(dashboard)/jobs/[tab]/restoreJobsCard';
import { ScanJobsCard } from '@/app/(dashboard)/jobs/[tab]/scanJobsCard';
import { IngestionPipelinePanel } from '@/app/(dashboard)/lakehouse/ingestion-pipelines/ingestionPipelinePanel';
import { IngestionPipelineWizardPanel } from '@/app/(dashboard)/lakehouse/ingestion-pipelines/wizard/IngestionPipelineWizardPanel';
import { UpdateIngestionPipelineWizardPanel } from '@/app/(dashboard)/lakehouse/ingestion-pipelines/wizard/UpdateIngestionPipelineWizardPanel';
import { JobTemplatePanel } from '@/app/(dashboard)/lakehouse/job-templates/jobTemplatePanel';
import { NotebookPanel } from '@/app/(dashboard)/lakehouse/notebooks/notebookPanel';
import { SqlQueryAnalyzerPanel } from '@/app/(dashboard)/lakehouse/query-editor/sqlQueryAnalyzerPanel';
import { QueryExecutionPanel } from '@/app/(dashboard)/lakehouse/query-executions/queryExecutionPanel';
import { SavedQueryPanel } from '@/app/(dashboard)/lakehouse/saved-queries/savedQueryPanel';
import { SparkEnginePanel } from '@/app/(dashboard)/lakehouse/spark-engines/sparkEnginePanel';
import { SparkJobPanel } from '@/app/(dashboard)/lakehouse/spark-jobs/sparkJobPanel';
import { SqlEnginePanel } from '@/app/(dashboard)/lakehouse/sql-engines/sqlEnginePanel';
import { BackupPolicyPanel } from '@/app/(dashboard)/settings/backup-policies/BackupPolicySidePanel';
import { BackupScopeResourcesPanel } from '@/app/(dashboard)/settings/backup-policies/backup-scopes/[tab]/components/backupScopeResourcesEditor';
import { ControlWizard } from '@/app/(dashboard)/settings/controls/ControlWizard';
import { ObjectFiltersPanel } from '@/app/(dashboard)/settings/object-filters/objectFiltersPanel';
import { RansomwareByConditionPanel } from '@/app/(dashboard)/settings/ransomware-detection/ransomwareByConditionPanel';
import { UpdateAccountAWSPanel } from '@/components/accounts/updateAccountAWSPanel';
import { UpdateAccountAzurePanel } from '@/components/accounts/updateAccountAzurePanel';
import { CreateGraphWizard } from '@/components/lakehouse/dashboards/createGraphWizard';

import { ConvertAllToEonSnapshotsPanel } from './ConvertAllToEonSnapshotsPanel';
import { ConvertToSingleEonSnapshotPanel } from './ConvertToSingleEonSnapshotPanel';
import type { Panels } from './panels';

import { MetricsPanel } from '../accounts/job-metrics/metricsPanel';
import { RecreateAccountRoleAWSPanel } from '../accounts/recreateAccountRoleAWSPanel';
import { RecreateAccountRoleGcpPanel } from '../accounts/recreateAccountRoleGcpPanel';
import { RestoreServerConnectivityPanel } from '../accounts/restore/restore-server-connectivity/restoreServerConnectivityPanel';
import { RestoreServerTagsPanel } from '../accounts/restore/restore-server-tags/restoreServerTagsPanel';
import { UpdateAccountGCPPanel } from '../accounts/updateAccountGCPPanel';
import { ConnectAwsVaultAccountPanel } from '../accounts/vault/connectAwsVaultAccountPanel';
import { ApiCredentialsPanel } from '../apiCredentials/apiCredentialsPanel';
import { RotateApiCredentialsPanel } from '../apiCredentials/rotateApiCredentialsPanel';
import { FiltersPanel } from '../filters/filtersPanel';
import { UpsertGroupMappingPanel } from '../groups/upsertGroupMappingPanel';
import { IntegrationWizard } from '../integrations/wizard/integrationWizard';
import { ConfigureProjectLimitsWizard } from '../limits/configureProjectLimitsWizard';
import { LogSpaceCard } from '../log-space/demo/logSpaceCard';
import { LogSpaceRecordCard } from '../log-space/prod/logSpaceRecordCard';
import { LogSpaceSchemaBrowser } from '../log-space/prod/logSpaceSchemaBrowser';
import { UpsertNotificationPolicyPanel } from '../notifications/upsertNotificationPolicyPanel';
import { CustomRolePanel } from '../permissions/customRolePanel';
import { ConvertToAMIWizard } from '../restore/convertToAMIWizard';
import { RestoreAzureDiskWizard } from '../restore/restoreAzureDiskWizard';
import { RestoreAzureSqlDBWizard } from '../restore/restoreAzureSqlDbWizard';
import { RestoreAzureVMWizard } from '../restore/restoreAzureVMWizard';
import { RestoreBigQueryWizard } from '../restore/restoreBigQueryWizard';
import { RestoreDynamoWizard } from '../restore/restoreDynamoWizard';
import { RestoreEc2InstanceWizard } from '../restore/restoreEc2InstanceWizard';
import { RestoreEFSWizard } from '../restore/restoreEfsWizard';
import { RestoreEksNamespaceWizard } from '../restore/restoreEksNamespaceWizard';
import { RestoreFileWizard } from '../restore/restoreFileWizard';
import { RestoreFSxWizard } from '../restore/restoreFsxWizard';
import { RestoreGcpCloudSqlWizard } from '../restore/restoreGcpCloudSqlWizard';
import { RestoreGcpDiskWizard } from '../restore/restoreGcpDiskWizard';
import { RestoreGcpVMWizard } from '../restore/restoreGcpVMWizard';
import { RestoreMongoAtlasWizard } from '../restore/restoreMongoAtlasWizard';
import { RestoreRDSPitrWizard } from '../restore/restoreRDSPitrWizard';
import { RestoreRDSWizard } from '../restore/restoreRDSWizard';
import { RestoreS3Wizard } from '../restore/restoreS3Wizard';
import { RestoreVolumeWizard } from '../restore/restoreVolumeWizard';
import { SamlPanel } from '../saml/samlPanel';
import { StorageGatewayWizard } from '../storage-gateway/wizard/storageGatewayWizard';
import { UpsertUserPanel } from '../users/upsertUserPanel';
import { CreateBackupVaultPanel } from '../vaults/createBackupVaultPanel';

type PanelsMap<T extends PanelTypes> = {
  [k in T['panel']]: (
    props: Extract<PanelTypes, { panel: k }>['props']
  ) => React.ReactNode;
};

export const PanelsMap: PanelsMap<PanelTypes> = {
  InventoryInstance: InventoryInstancePanel,
  RestoreVolumeWizard: RestoreVolumeWizard,
  RestoreEc2InstanceWizard: RestoreEc2InstanceWizard,
  RestoreFileWizard: RestoreFileWizard,
  RestoreRDSWizard: RestoreRDSWizard,
  RestoreRDSPitrWizard: RestoreRDSPitrWizard,
  RestoreDynamoWizard: RestoreDynamoWizard,
  RestoreS3Wizard: RestoreS3Wizard,
  ConvertToAMIWizard: ConvertToAMIWizard,
  RestoreMongoAtlasWizard: RestoreMongoAtlasWizard,
  RestoreAzureVMWizard: RestoreAzureVMWizard,
  RestoreAzureDiskWizard: RestoreAzureDiskWizard,
  RestoreAzureSqlDBWizard: RestoreAzureSqlDBWizard,
  CreateBackupVault: CreateBackupVaultPanel,
  BackupPolicyConfig: BackupPolicyPanel,
  CustomRole: CustomRolePanel,
  ApiCredentials: ApiCredentialsPanel,
  RotateApiCredentials: RotateApiCredentialsPanel,
  Control: ControlWizard,
  NotificationPolicy: UpsertNotificationPolicyPanel,
  SAML: SamlPanel,
  Filters: FiltersPanel,
  Integration: IntegrationWizard,
  RecreateAccountRoleAWSPanel: RecreateAccountRoleAWSPanel,
  RecreateAccountRoleGCPPanel: RecreateAccountRoleGcpPanel,
  ConnectAwsVaultAccount: ConnectAwsVaultAccountPanel,
  UpsertUser: UpsertUserPanel,
  UpsertGroupMapping: UpsertGroupMappingPanel,
  RestoreServerConnectivityPanel: RestoreServerConnectivityPanel,
  RestoreServerTagsPanel: RestoreServerTagsPanel,
  MetricsPanel: MetricsPanel,
  ConvertToMultipleEonSnapshots: ConvertAllToEonSnapshotsPanel,
  ConvertToSingleEonSnapshot: ConvertToSingleEonSnapshotPanel,
  RestoreEksNamespaceWizard: RestoreEksNamespaceWizard,
  ObjectStoreBackupConfiguration: ObjectFiltersPanel,
  RestoreGcpVMWizard: RestoreGcpVMWizard,
  RestoreGcpDiskWizard: RestoreGcpDiskWizard,
  RestoreGcpCloudSqlWizard: RestoreGcpCloudSqlWizard,
  RestoreBigQueryWizard: RestoreBigQueryWizard,
  RestoreFSxWizard: RestoreFSxWizard,
  RestoreEFSWizard: RestoreEFSWizard,
  UpdateAccountAWSPanel: UpdateAccountAWSPanel,
  UpdateAccountGCPPanel: UpdateAccountGCPPanel,
  UpdateAccountAzurePanel: UpdateAccountAzurePanel,
  RansomwareByConditionPanel: RansomwareByConditionPanel,
  BackupScopeResourcesPanel: BackupScopeResourcesPanel,
  BackupJobsCard: BackupJobsCard,
  ScanJobsCard: ScanJobsCard,
  RestoreJobsCard: RestoreJobsCard,
  AzureOwnCloudOnboardingWizard: AzureOwnCloudOnboardingWizard,
  AzureOnboardingWizard: AzureOnboardingWizard,
  AzureConnectRestoreWizard: AzureConnectRestoreWizard,
  AzureConnectSourceWizard: AzureConnectSourceWizard,
  AzureConnectTenantWizard: AzureConnectTenantWizard,
  AwsConnectAccountWizard: AwsConnectAccountWizard,
  StorageGatewayWizard: StorageGatewayWizard,
  ConfigureProjectLimitsWizard: ConfigureProjectLimitsWizard,
  LogSpaceSchemaBrowser: LogSpaceSchemaBrowser,
  LogSpaceCard: LogSpaceCard,
  LogSpaceRecordCard: LogSpaceRecordCard,
  GcpConnectAccountWizard: GcpConnectAccountWizard,
  SparkEngineInstance: SparkEnginePanel,
  SqlEngineInstance: SqlEnginePanel,
  JobTemplateInstance: JobTemplatePanel,
  SavedQueryInstance: SavedQueryPanel,
  IngestionPipelineInstance: IngestionPipelinePanel,
  NotebookInstance: NotebookPanel,
  QueryExecutionInstance: QueryExecutionPanel,
  SparkJobInstance: SparkJobPanel,
  CreateGraphWizard: CreateGraphWizard,
  SqlQueryAnalyzer: SqlQueryAnalyzerPanel,
  CreateIngestionPipelineWizard: IngestionPipelineWizardPanel,
  UpdateIngestionPipelineWizard: UpdateIngestionPipelineWizardPanel,
};

export type PanelTypes =
  | {
      panel: typeof Panels.InventoryInstance;
      props: ComponentProps<typeof InventoryInstancePanel>;
    }
  | {
      panel: typeof Panels.CreateBackupVault;
      props: ComponentProps<typeof CreateBackupVaultPanel>;
    }
  | {
      panel: typeof Panels.RestoreVolumeWizard;
      props: ComponentProps<typeof RestoreVolumeWizard>;
    }
  | {
      panel: typeof Panels.RestoreEc2InstanceWizard;
      props: ComponentProps<typeof RestoreEc2InstanceWizard>;
    }
  | {
      panel: typeof Panels.RestoreFileWizard;
      props: ComponentProps<typeof RestoreFileWizard>;
    }
  | {
      panel: typeof Panels.RestoreRDSWizard;
      props: ComponentProps<typeof RestoreRDSWizard>;
    }
  | {
      panel: typeof Panels.RestoreRDSPitrWizard;
      props: ComponentProps<typeof RestoreRDSPitrWizard>;
    }
  | {
      panel: typeof Panels.RestoreDynamoWizard;
      props: ComponentProps<typeof RestoreDynamoWizard>;
    }
  | {
      panel: typeof Panels.RestoreS3Wizard;
      props: ComponentProps<typeof RestoreS3Wizard>;
    }
  | {
      panel: typeof Panels.RestoreMongoAtlasWizard;
      props: ComponentProps<typeof RestoreMongoAtlasWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureVMWizard;
      props: ComponentProps<typeof RestoreAzureVMWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureDiskWizard;
      props: ComponentProps<typeof RestoreAzureDiskWizard>;
    }
  | {
      panel: typeof Panels.RestoreAzureSqlDBWizard;
      props: ComponentProps<typeof RestoreAzureSqlDBWizard>;
    }
  | {
      panel: typeof Panels.ConvertToAMIWizard;
      props: ComponentProps<typeof ConvertToAMIWizard>;
    }
  | {
      panel: typeof Panels.NotificationPolicy;
      props: ComponentProps<typeof UpsertNotificationPolicyPanel>;
    }
  | {
      panel: typeof Panels.SAML;
      props: ComponentProps<typeof SamlPanel>;
    }
  | {
      panel: typeof Panels.Filters;
      props: ComponentProps<typeof FiltersPanel>;
    }
  | {
      panel: typeof Panels.BackupPolicyConfig;
      props: ComponentProps<typeof BackupPolicyPanel>;
    }
  | {
      panel: typeof Panels.CustomRole;
      props: ComponentProps<typeof CustomRolePanel>;
    }
  | {
      panel: typeof Panels.ApiCredentials;
      props: ComponentProps<typeof ApiCredentialsPanel>;
    }
  | {
      panel: typeof Panels.RotateApiCredentials;
      props: ComponentProps<typeof RotateApiCredentialsPanel>;
    }
  | {
      panel: typeof Panels.Control;
      props: ComponentProps<typeof ControlWizard>;
    }
  | {
      panel: typeof Panels.RecreateAccountRoleAWSPanel;
      props: ComponentProps<typeof RecreateAccountRoleAWSPanel>;
    }
  | {
      panel: typeof Panels.RecreateAccountRoleGCPPanel;
      props: ComponentProps<typeof RecreateAccountRoleGcpPanel>;
    }
  | {
      panel: typeof Panels.ConnectAwsVaultAccount;
      props: ComponentProps<typeof ConnectAwsVaultAccountPanel>;
    }
  | {
      panel: typeof Panels.UpdateAccountAWSPanel;
      props: ComponentProps<typeof UpdateAccountAWSPanel>;
    }
  | {
      panel: typeof Panels.UpdateAccountGCPPanel;
      props: ComponentProps<typeof UpdateAccountGCPPanel>;
    }
  | {
      panel: typeof Panels.UpdateAccountAzurePanel;
      props: ComponentProps<typeof UpdateAccountAzurePanel>;
    }
  | {
      panel: typeof Panels.UpsertUser;
      props: ComponentProps<typeof UpsertUserPanel>;
    }
  | {
      panel: typeof Panels.UpsertGroupMapping;
      props: ComponentProps<typeof UpsertGroupMappingPanel>;
    }
  | {
      panel: typeof Panels.RestoreServerConnectivityPanel;
      props: ComponentProps<typeof RestoreServerConnectivityPanel>;
    }
  | {
      panel: typeof Panels.RestoreServerTagsPanel;
      props: ComponentProps<typeof RestoreServerTagsPanel>;
    }
  | {
      panel: typeof Panels.MetricsPanel;
      props: ComponentProps<typeof MetricsPanel>;
    }
  | {
      panel: typeof Panels.ConvertToMultipleEonSnapshots;
      props: ComponentProps<typeof ConvertAllToEonSnapshotsPanel>;
    }
  | {
      panel: typeof Panels.ConvertToSingleEonSnapshot;
      props: ComponentProps<typeof ConvertToSingleEonSnapshotPanel>;
    }
  | {
      panel: typeof Panels.RestoreEksNamespaceWizard;
      props: ComponentProps<typeof RestoreEksNamespaceWizard>;
    }
  | {
      panel: typeof Panels.ObjectStoreBackupConfiguration;
      props: ComponentProps<typeof ObjectFiltersPanel>;
    }
  | {
      panel: typeof Panels.RestoreGcpVMWizard;
      props: ComponentProps<typeof RestoreGcpVMWizard>;
    }
  | {
      panel: typeof Panels.RestoreGcpDiskWizard;
      props: ComponentProps<typeof RestoreGcpDiskWizard>;
    }
  | {
      panel: typeof Panels.RestoreGcpCloudSqlWizard;
      props: ComponentProps<typeof RestoreGcpCloudSqlWizard>;
    }
  | {
      panel: typeof Panels.RestoreBigQueryWizard;
      props: ComponentProps<typeof RestoreBigQueryWizard>;
    }
  | {
      panel: typeof Panels.RestoreFSxWizard;
      props: ComponentProps<typeof RestoreFSxWizard>;
    }
  | {
      panel: typeof Panels.RestoreEFSWizard;
      props: ComponentProps<typeof RestoreEFSWizard>;
    }
  | {
      panel: typeof Panels.RansomwareConditionPanel;
      props: ComponentProps<typeof RansomwareByConditionPanel>;
    }
  | {
      panel: typeof Panels.BackupScopeResourcesPanel;
      props: ComponentProps<typeof BackupScopeResourcesPanel>;
    }
  | {
      panel: typeof Panels.ScanJobsCard;
      props: ComponentProps<typeof ScanJobsCard>;
    }
  | {
      panel: typeof Panels.BackupJobsCard;
      props: ComponentProps<typeof BackupJobsCard>;
    }
  | {
      panel: typeof Panels.RestoreJobsCard;
      props: ComponentProps<typeof RestoreJobsCard>;
    }
  | {
      panel: typeof Panels.AzureOwnCloudOnboardingWizard;
      props: ComponentProps<typeof AzureOwnCloudOnboardingWizard>;
    }
  | {
      panel: typeof Panels.AzureOnboardingWizard;
      props: ComponentProps<typeof AzureOnboardingWizard>;
    }
  | {
      panel: typeof Panels.AzureConnectRestoreWizard;
      props: ComponentProps<typeof AzureConnectRestoreWizard>;
    }
  | {
      panel: typeof Panels.AzureConnectSourceWizard;
      props: ComponentProps<typeof AzureConnectSourceWizard>;
    }
  | {
      panel: typeof Panels.AwsConnectAccountWizard;
      props: ComponentProps<typeof AwsConnectAccountWizard>;
    }
  | {
      panel: typeof Panels.GcpConnectAccountWizard;
      props: ComponentProps<typeof GcpConnectAccountWizard>;
    }
  | {
      panel: typeof Panels.AzureConnectTenantWizard;
      props: ComponentProps<typeof AzureConnectTenantWizard>;
    }
  | {
      panel: typeof Panels.StorageGatewayWizard;
      props: ComponentProps<typeof StorageGatewayWizard>;
    }
  | {
      panel: typeof Panels.ConfigureProjectLimitsWizard;
      props: ComponentProps<typeof ConfigureProjectLimitsWizard>;
    }
  | {
      panel: typeof Panels.Integration;
      props: ComponentProps<typeof IntegrationWizard>;
    }
  | {
      panel: typeof Panels.LogSpaceSchemaBrowser;
      props: ComponentProps<typeof LogSpaceSchemaBrowser>;
    }
  | {
      panel: typeof Panels.LogSpaceCard;
      props: ComponentProps<typeof LogSpaceCard>;
    }
  | {
      panel: typeof Panels.LogSpaceRecordCard;
      props: ComponentProps<typeof LogSpaceRecordCard>;
    }
  | {
      panel: typeof Panels.SparkEngineInstance;
      props: ComponentProps<typeof SparkEnginePanel>;
    }
  | {
      panel: typeof Panels.SqlEngineInstance;
      props: ComponentProps<typeof SqlEnginePanel>;
    }
  | {
      panel: typeof Panels.JobTemplateInstance;
      props: ComponentProps<typeof JobTemplatePanel>;
    }
  | {
      panel: typeof Panels.SavedQueryInstance;
      props: ComponentProps<typeof SavedQueryPanel>;
    }
  | {
      panel: typeof Panels.IngestionPipelineInstance;
      props: ComponentProps<typeof IngestionPipelinePanel>;
    }
  | {
      panel: typeof Panels.NotebookInstance;
      props: ComponentProps<typeof NotebookPanel>;
    }
  | {
      panel: typeof Panels.QueryExecutionInstance;
      props: ComponentProps<typeof QueryExecutionPanel>;
    }
  | {
      panel: typeof Panels.SparkJobInstance;
      props: ComponentProps<typeof SparkJobPanel>;
    }
  | {
      panel: typeof Panels.CreateGraphWizard;
      props: ComponentProps<typeof CreateGraphWizard>;
    }
  | {
      panel: typeof Panels.SqlQueryAnalyzer;
      props: ComponentProps<typeof SqlQueryAnalyzerPanel>;
    }
  | {
      panel: typeof Panels.CreateIngestionPipelineWizard;
      props: ComponentProps<typeof IngestionPipelineWizardPanel>;
    }
  | {
      panel: typeof Panels.UpdateIngestionPipelineWizard;
      props: ComponentProps<typeof UpdateIngestionPipelineWizardPanel>;
    };
